import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  DialogContent,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import StandardDialog from "../../components/StandardDialog.js";
import { useEffect, useRef, useState } from "react";
import { useThemeContext } from "../../context/themeContext.js";
import PinIcon from "@mui/icons-material/Pin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../hooks/useApi.js";
import { getContentOptionsArray, handleApiError } from "../../utils/utils.js";
import VisibilitySelector from "../../components/VisibilitySelector.js";
import { invalidateListCaches } from "../../utils/cacheUtils.js";
import { useAuth } from "../../context/authContext.js";

const contentTypeOptions = getContentOptionsArray({ showMixedOption: true });

/**
 * Derive a URL-safe slug from a display name.
 * Mirrors the backend _derive_slug logic so the preview is accurate.
 */
function deriveSlug(displayName) {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Inline form for creating a brand-new tag. Shown when the admin clicks "New tag".
 * Calls onCreated(tag) with the newly created tag object on success.
 */
function NewTagForm({ existingKinds, onCreated, onCancel }) {
  const { createListTag } = useApi();
  const [kind, setKind] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  const mutation = useMutation({
    mutationFn: createListTag,
    onSuccess: (tag) => onCreated(tag),
    onError: handleApiError,
  });

  const handleDisplayNameChange = (value) => {
    setDisplayName(value);
    // Auto-derive slug until the user manually edits it
    if (!slugTouched) setSlug(deriveSlug(value));
  };

  const handleSlugChange = (value) => {
    setSlug(value);
    setSlugTouched(true);
  };

  const canSubmit = kind.trim() && displayName.trim() && slug.trim();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
      {/* Kind combobox — freeSolo so new kinds can be typed */}
      <Autocomplete
        freeSolo
        options={existingKinds}
        inputValue={kind}
        onInputChange={(_, value) => setKind(value)}
        size="small"
        renderInput={(params) => (
          <TextField {...params} label="Kind" placeholder="e.g. era, source" />
        )}
      />

      <TextField
        size="small"
        label="Display name"
        value={displayName}
        onChange={(e) => handleDisplayNameChange(e.target.value)}
      />

      <TextField
        size="small"
        label="Slug"
        value={slug}
        onChange={(e) => handleSlugChange(e.target.value)}
        helperText="Auto-derived from display name; edit to override"
      />

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button size="small" onClick={onCancel} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            mutation.mutate({
              displayName: displayName.trim(),
              kind: kind.trim(),
              slug: slug.trim(),
            })
          }
          disabled={!canSubmit || mutation.isPending}
        >
          {mutation.isPending ? <CircularProgress size={16} /> : "Create tag"}
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * Handles both collection creation and editing in a single dialog.
 * Pass `existingList` to enter edit mode; omit it (or pass null) for create mode.
 * Admin users see an additional origin toggle and tag picker.
 */
export default function CollectionDialog({
  open,
  onClose,
  onAdd, // create mode: called with new list ID on success
  contentType, // create mode: initial content type selection
  contentIdToAdd, // create mode: content to add to the new collection
  existingList, // edit mode: the collection being edited
}) {
  const { user } = useAuth();
  const { isTouchDevice } = useThemeContext();
  const { createList, editListDetails, getListTags } = useApi();
  const queryClient = useQueryClient();
  const titleInputRef = useRef(null);

  const isEdit = Boolean(existingList);
  const isAdmin = Boolean(user?.isAdmin);

  const initialContentType =
    existingList?.content_type ??
    (contentType === "all" ? user?.preferences.content : contentType);

  const buildState = () =>
    isEdit
      ? {
          title: existingList.title,
          description: existingList.description || "",
          // Use the new origin field directly; fall back to "user" for older data
          origin: existingList.origin ?? "user",
          isRanked: existingList.is_ranked,
          visibility: existingList.visibility || "only_me",
          contentType: existingList.content_type,
        }
      : {
          title: "",
          description: "",
          origin: "user",
          isRanked: false,
          visibility: "everyone",
          contentType: initialContentType,
          contentIdToAdd,
        };

  const [listState, setListState] = useState(buildState);

  // Tag IDs for admin tag assignment (only used when origin is "canonical")
  const [tagIds, setTagIds] = useState(
    existingList?.tags?.map((t) => t.id) ?? [],
  );
  const [showNewTagForm, setShowNewTagForm] = useState(false);

  // Re-sync state whenever the dialog opens so stale values don't persist
  useEffect(() => {
    if (open) {
      setListState(buildState());
      setTagIds(existingList?.tags?.map((t) => t.id) ?? []);
      setShowNewTagForm(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (name, value) =>
    setListState((prev) => ({ ...prev, [name]: value }));

  // Fetch tag list only when the admin tag section is visible
  const { data: tagData, isLoading: tagsLoading } = useQuery({
    queryKey: ["listTags"],
    queryFn: getListTags,
    enabled: isAdmin && listState.origin === "canonical",
    staleTime: 5 * 60 * 1000, // tags change infrequently
  });

  // Flatten groups → array for Autocomplete; also derive unique kinds list
  const flatTagList = tagData ? Object.values(tagData.groups).flat() : [];
  const existingKinds = tagData ? Object.keys(tagData.groups).sort() : [];

  // Tag objects currently selected (resolved from IDs for Autocomplete value)
  const selectedTags = flatTagList.filter((t) => tagIds.includes(t.id));

  const mutation = useMutation({
    mutationFn: (params) =>
      isEdit ? editListDetails(params) : createList(params),
    onSuccess: (data) => {
      // listId omitted on create — nothing is cached under the new id yet.
      invalidateListCaches({
        queryClient,
        listId: isEdit ? existingList.list_id : undefined,
        contentType: listState.contentType,
      });
      if (!isEdit) onAdd?.(data.id);
      onClose?.();
    },
    onError: handleApiError,
  });

  const handleSubmit = () => {
    if (isEdit) {
      mutation.mutate({
        listId: existingList.list_id,
        title: listState.title,
        description: listState.description,
        isRanked: listState.isRanked,
        origin: listState.origin,
        visibility: listState.visibility,
        size: null, // size limit is deprecated; cleared on edit
        ...(isAdmin && { tagIds }),
      });
    } else {
      mutation.mutate({
        ...listState,
        origin: listState.origin,
        ...(isAdmin && { tagIds }),
      });
    }
  };

  const handleNewTagCreated = (tag) => {
    // Invalidate cached tag list so new kind/tag appears in future loads
    queryClient.invalidateQueries({ queryKey: ["listTags"] });
    setTagIds((prev) => [...prev, tag.id]);
    setShowNewTagForm(false);
  };

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        onEntered: () => {
          if (!isTouchDevice) titleInputRef.current?.focus();
        },
      }}
    >
      <DialogContent sx={{ p: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Title */}
          <TextField
            id="collection-title-input"
            variant="standard"
            hiddenLabel
            placeholder="Collection name"
            fullWidth
            inputRef={titleInputRef}
            multiline
            maxRows={2}
            value={listState.title}
            onChange={(e) => updateField("title", e.target.value)}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: (theme) => theme.typography.h6.fontSize,
                fontWeight: (theme) => theme.typography.h6.fontWeight,
                fontFamily: (theme) => theme.typography.h6.fontFamily,
                lineHeight: (theme) => theme.typography.h6.lineHeight,
                p: 0,
              },
            }}
          />

          {/* Description */}
          <TextField
            id="collection-description-input"
            variant="standard"
            hiddenLabel
            placeholder="Description"
            fullWidth
            multiline
            minRows={3}
            maxRows={5}
            value={listState.description}
            onChange={(e) => updateField("description", e.target.value)}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: (theme) => theme.typography.body2.fontSize,
                fontWeight: (theme) => theme.typography.body2.fontWeight,
                fontFamily: (theme) => theme.typography.body2.fontFamily,
                lineHeight: (theme) => theme.typography.body2.lineHeight,
                p: 0,
              },
            }}
          />

          {/* Content type chips + ranked chip */}
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
          >
            {contentTypeOptions.map((option) => (
              <Chip
                key={option.value}
                icon={option.icon}
                label={option.labelPlural}
                size="small"
                onClick={
                  isEdit
                    ? undefined
                    : () => updateField("contentType", option.value)
                }
                disabled={isEdit}
                color={
                  listState.contentType === option.value ? "primary" : "default"
                }
              />
            ))}

            <Box component="span" sx={{ color: "text.disabled", mx: 0.75 }}>
              •
            </Box>

            {/*
             * ButtonBase instead of Chip: MUI Chip doesn't support custom palette
             * colours (like tertiary) via the color prop, and its hover/focus styles
             * have higher specificity than sx overrides, making them impossible to
             * reliably override without !important. ButtonBase has no pre-baked
             * background or hover styles, so our sx has full control.
             */}
            <ButtonBase
              onClick={() => updateField("isRanked", !listState.isRanked)}
              focusRipple
              sx={(theme) => ({
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                height: "24px",
                borderRadius: "16px",
                border: "1px solid",
                px: "8px",
                fontSize: "0.8125rem",
                transition: theme.transitions.create([
                  "background-color",
                  "border-color",
                  "color",
                ]),
                ...(listState.isRanked
                  ? {
                      backgroundColor: theme.palette.tertiary.main,
                      borderColor: theme.palette.tertiary.main,
                      color: theme.palette.tertiary.contrastText,
                      "&:hover": {
                        backgroundColor: theme.palette.tertiary.dark,
                        borderColor: theme.palette.tertiary.dark,
                      },
                    }
                  : {
                      backgroundColor: "transparent",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }),
              })}
            >
              <PinIcon sx={{ fontSize: "1rem" }} />
              Ranked
            </ButtonBase>
          </Stack>

          {/* Admin: origin toggle */}
          {isAdmin && (
            <>
              <Divider sx={{ pt: 1 }} />
              <Typography variant="body2">Admin controls</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Origin
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={listState.origin}
                  onChange={(_, value) => {
                    // ToggleButtonGroup passes null when deselecting — keep current value
                    if (value !== null) updateField("origin", value);
                  }}
                >
                  <ToggleButton value="user">User</ToggleButton>
                  <ToggleButton value="canonical">Canonical</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </>
          )}

          {/* Admin: tag picker (only visible when origin is canonical) */}
          {isAdmin && listState.origin === "canonical" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {tagsLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <Autocomplete
                    multiple
                    options={flatTagList}
                    groupBy={(t) => t.kind}
                    getOptionLabel={(t) => `${t.kind}:${t.slug}`}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    value={selectedTags}
                    onChange={(_, newValue) =>
                      setTagIds(newValue.map((t) => t.id))
                    }
                    renderOption={(props, tag) => (
                      <li {...props} key={tag.id}>
                        {tag.slug}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tags"
                        size="small"
                        placeholder="Add tags…"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((tag, index) => (
                        <Chip
                          key={tag.id}
                          label={`${tag.kind}:${tag.slug}`}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />

                  {/* New tag disclosure */}
                  {showNewTagForm ? (
                    <NewTagForm
                      existingKinds={existingKinds}
                      onCreated={handleNewTagCreated}
                      onCancel={() => setShowNewTagForm(false)}
                    />
                  ) : (
                    <Button
                      size="small"
                      sx={{ alignSelf: "flex-start" }}
                      onClick={() => setShowNewTagForm(true)}
                    >
                      + New tag
                    </Button>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Visibility + action buttons */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 1 }}
          >
            <VisibilitySelector
              value={listState.visibility}
              onChange={(value) => updateField("visibility", value)}
            />
            <Stack direction="row" spacing={1}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={mutation.isPending || !listState.title}
              >
                {isEdit ? "Save" : "Create"}
              </Button>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
    </StandardDialog>
  );
}
